import type { FC } from 'react'
import React from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import type { ITheme } from '@homeviews/styles'
import { Typography, StarRating, Button } from '@homeviews/shared-ui'
import { maybePlaceholderImage } from '@homeviews/utils'
import { mixpanelTrack } from '@homeviews/seo'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      '&:hover': {
        cursor: 'pointer',
      },
    },
    header: {
      height: 0,
      position: 'relative',
      borderRadius: '3px',
      overflow: 'hidden',
      paddingBottom: `${(204 / 317) * 100}%`,
      '&:after': {
        content: '""',
        backgroundColor: 'rgba(0,0,0,0.02)',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      },
      '& img': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
    },
    body: {
      flex: 1,
      paddingTop: theme.spacing(1.375),
    },
    title: {},
    meta: {
      display: 'flex',
    },
    metaEl: {
      color: theme.colors.grey450,
      marginRight: theme.spacing(0.9375),
      marginTop: theme.spacing(0.625),
      marginBottom: theme.spacing(0.625),
    },
    metaElNoMargin: {
      marginTop: 0,
    },
    starRatingWrapper: {
      marginTop: 2,
    },
    ratingWrapper: {
      fontSize: 12,
      '& span': {
        color: '#434343',
        fontWeight: 500,
      },
    },
    editButton: {
      position: 'absolute',
      bottom: 10,
      right: 10,
    },
  }),
)
interface IProps {
  className?: string
  id: number
  name: string
  rating: string
  imageSrc?: string
  area?: string
  developers?: any
  noReviews?: number
  rankedOutOf?: string
  changeInRanking?: string
  currentRanking?: string
  isEditable?: boolean
}

const DevelopmentCard: FC<IProps> = ({
  className,
  id,
  name,
  rating,
  imageSrc,
  area,
  developers,
  noReviews,
  isEditable,
}) => {
  const classes = useStyles()

  const handleClick = () => {
    mixpanelTrack('DEVELOPMENTS_VIEW_DEVELOPMENT', {
      value: name,
    })
  }

  return (
    <Link
      to={`/developments/${id}`}
      className={clsx(classes.root, className)}
      onClick={handleClick}
    >
      <div className={classes.header}>
        <img src={maybePlaceholderImage(imageSrc)} alt={`${name}`} />
        {isEditable && (
          <Button
            className={clsx(
              classes.editButton,
              'c-developmentCard__editButton',
            )}
            variant="contained"
            color="primary"
            customColor="primary"
            size="small"
          >
            Edit
          </Button>
        )}
      </div>
      <div className={classes.body}>
        <Typography variant="h5" className={classes.title}>
          {name}
        </Typography>
        <div className={classes.meta}>
          <Typography className={classes.metaEl}>
            {developers?.map((developer: any) => developer?.name)?.join(', ')},
          </Typography>
          {!!area && (
            <Typography className={classes.metaEl}> {area}</Typography>
          )}
        </div>
        <div className={classes.meta}>
          <div
            className={clsx(
              classes.metaEl,
              classes.metaElNoMargin,
              classes.starRatingWrapper,
            )}
          >
            <StarRating value={parseFloat(rating)} fontSize={16} />
          </div>
          <Typography
            className={clsx(
              classes.metaEl,
              classes.ratingWrapper,
              classes.metaElNoMargin,
            )}
          >
            <span style={{ marginLeft: '8px' }}>{rating} </span>{' '}
            {noReviews &&
              `(${noReviews} ${noReviews === 1 ? 'review' : 'reviews'})`}
          </Typography>
        </div>
      </div>
    </Link>
  )
}

export default DevelopmentCard
